import { useCallback, useRef, useState } from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import { triggerFileDownload } from 'web/utils/triggerFileDownload';

export const timeSliceInSeconds = 2;

interface AudioRecorderProps {
    processPCM: (pcmData: Blob) => void;
}

export enum MicrophoneErrorType {
    NOT_FOUND = 'NOT_FOUND',
    PERMISSION_DENIED = 'PERMISSION_DENIED',
    GENERAL_ERROR = 'GENERAL_ERROR',
}

export interface MicrophoneError {
    type: MicrophoneErrorType;
    message: string;
}

const useAudioRecorder = ({ processPCM }: AudioRecorderProps) => {
    const recorder = useRef<RecordRTC | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const timeSlice = timeSliceInSeconds * 1000;
    const sampleRate = 16000;
    const [microphoneError, setMicrophoneError] = useState<MicrophoneError | null>(null);

    const handleDataAvailable = useCallback((blob: Blob) => {
        if (blob.size > 0) {
            processPCM(blob);
        }
    }, [processPCM]);

    const startRecording = useCallback(() => {
        // Reset any previous error
        setMicrophoneError(null);

        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
            streamRef.current = stream;
            recorder.current = new RecordRTC(stream, {
                type: 'audio',
                mimeType: 'audio/wav',
                recorderType: StereoAudioRecorder,
                desiredSampRate: sampleRate,
                numberOfAudioChannels: 1,
                timeSlice: timeSlice,
                ondataavailable: handleDataAvailable
            });
            recorder.current.startRecording();
        }).catch(error => {
            console.error('Microphone access error:', error);

            // Handle specific error types
            if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                setMicrophoneError({
                    type: MicrophoneErrorType.NOT_FOUND,
                    message: 'No microphone found. Please connect a microphone and try again.'
                });
            } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setMicrophoneError({
                    type: MicrophoneErrorType.PERMISSION_DENIED,
                    message: 'Microphone access denied. Please allow microphone access in your browser settings.'
                });
            } else {
                setMicrophoneError({
                    type: MicrophoneErrorType.GENERAL_ERROR,
                    message: 'An error occurred while accessing the microphone. Please try again.'
                });
            }
        });
    }, [processPCM, timeSlice, sampleRate, handleDataAvailable]);

    const stopRecording = useCallback((isEnableDownload: boolean) => {
        try {
            recorder.current?.stopRecording(() => {
                let blob = recorder.current?.getBlob();
                if (blob && isEnableDownload) {
                    const time = Date.now();
                    const fileName = `intake-recording-${time}.wav`;
                    triggerFileDownload(blob, fileName);
                }
                // Always clean up the media stream tracks
                streamRef.current?.getTracks().forEach(track => track.stop());
            });
        } catch (error) {
            console.error('Error stopping recording:', error);
            // Clean up the media stream tracks even if there's an error
            try {
                streamRef.current?.getTracks().forEach(track => track.stop());
            } catch (cleanupError) {
                console.error('Error cleaning up media stream:', cleanupError);
            }
        }
    }, []);

    const togglePauseResume = useCallback(() => {
        if (!recorder.current) return;

        if (recorder.current.getState() === 'paused') {
            recorder.current.resumeRecording();
        } else {
            recorder.current.pauseRecording();
        }
    }, []);

    return {
        startRecording,
        stopRecording,
        togglePauseResume,
        microphoneError,
        get isRecording() { return recorder.current?.getState() === 'recording' },
        get isPaused() { return recorder.current?.getState() === 'paused' },
        sampleRate
    };
};

export default useAudioRecorder;
